import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DisplayGridColumnSettings } from 'src/app/models/gridColumnSettings.model';
import { GridSortSettings } from 'src/app/models/gridSortSettings.model';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';

enum ShowFilterEnum {
  All = 'all',
  Selected = 'selected',
  Unselected = 'unselected'
}

export interface ExportWizardDialogData {
  viewName: string;
  allColumns: DisplayGridColumnSettings[];
  currentSort: GridSortSettings | null;
  filter: string;
  filterBy: string;
  dateFormat: number;
  timeFormat: number;
}

export interface ExportColumn {
  property: string;
  displayName: string;
  selected: boolean;
  order: number;
  visible: boolean;
  exportProperty?: string;
}

@Component({
  standalone: false,
  selector: 'app-export-wizard-dialog',
  templateUrl: './export-wizard-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./export-wizard-dialog.component.scss']
})
export class ExportWizardDialogComponent implements OnInit {
  showFilterEnum = ShowFilterEnum;
  exportColumns: ExportColumn[] = [];
  searchQuery: string = '';
  selectAll: boolean = false;
  showFilter: ShowFilterEnum = ShowFilterEnum.All;

  constructor(
    public dialogRef: MatDialogRef<ExportWizardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ExportWizardDialogData,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.initializeColumns();
    this.updateSelectAllState();
  }

  private initializeColumns(): void {
    this.exportColumns = this.data.allColumns
      .filter(col => col.property !== 'action' && col.isDisplayNameConfigurable !== false && col.isExportable !== false)
      .map(col => ({
        property: col.property,
        displayName: this.translate.instant(col.displayName),
        selected: col.visible,
        order: col.order,
        visible: col.visible,
        exportProperty: col.exportProperty
      }))
      .sort((a, b) => a.order - b.order);
  }

  get filteredColumns(): ExportColumn[] {
    let columns = this.exportColumns;

    if (this.showFilter === ShowFilterEnum.Selected) {
      columns = columns.filter(c => c.selected);
    } else if (this.showFilter === ShowFilterEnum.Unselected) {
      columns = columns.filter(c => !c.selected);
    }

    if (!this.searchQuery.trim()) {
      return columns;
    }

    const query = this.searchQuery.trim().toLowerCase();
    return columns.filter(col =>
      col.displayName.toLowerCase().includes(query) ||
      col.property.toLowerCase().includes(query)
    );
  }

  get selectedCount(): number {
    return this.exportColumns.filter(c => c.selected).length;
  }

  toggleSelectAll(): void {
    const filtered = this.filteredColumns;
    const allSelected = filtered.every(c => c.selected);
    filtered.forEach(c => { c.selected = !allSelected; });
    this.updateSelectAllState();
  }

  onColumnToggle(): void {
    this.updateSelectAllState();
  }

  updateSelectAllState(assignFilter: ShowFilterEnum | null = null): void {
    if (assignFilter) {
      this.showFilter = assignFilter;
    }
    const filtered = this.filteredColumns;
    this.selectAll = filtered.length > 0 && filtered.every(c => c.selected);
  }

  selectViewColumns(): void {
    this.exportColumns.forEach(c => { c.selected = c.visible; });
    this.updateSelectAllState();
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onExport(): void {
    const selectedColumns = this.exportColumns
      .filter(c => c.selected)
      .map((c, index) => ({
        property: c.exportProperty ?? c.property,
        displayName: c.displayName,
        visible: true,
        order: index
      }));

    if (selectedColumns.length === 0) {
      return;
    }

    this.dialogRef.close({
      viewName: this.data.viewName,
      columns: selectedColumns,
      sort: this.data.currentSort,
      filter: this.data.filter,
      filterBy: this.data.filterBy,
      dateFormat: this.data.dateFormat,
      timeFormat: this.data.timeFormat
    });
  }

  public trackByIndex = trackByIndex;
}
